import { useMemo } from "react";

import { groupBy, meanBy, partition, sortBy } from "lodash";

import { getClosestGrade } from "../../../utils/reviewUtils";
import { AnswerFragment } from '../../../graphql/generated/definitions';

export default function useEvolutionChartData(
  answers: AnswerFragment[],
  userId: string,
  options: { value: number; label: string }[]
) {
  return useMemo(() => {
    if (answers && answers.length > 0) {
      const answersPerReport = groupBy(answers, "reportId");

      const data = Object.entries(answersPerReport).flatMap(
        ([reportId, answers]) => {
          // The grading has been made outside of a report
          if (!reportId) {
            return [
              ...answers.map(({ collaborator, createdAt, normalizedRating }) => ({
                user: collaborator.name,
                reportId,
                date: new Date(createdAt),
                gradeType: "referents",
                grade: normalizedRating,
              })),
            ];
          }

          const [collaboratorAnswers, referentAnswers] = partition(answers, {
            isCollaborator: true,
          });

          const collaboratorAnswer = collaboratorAnswers?.[0];

          if (!collaboratorAnswer) return [];

          const nonNullReferentAnswers = referentAnswers.filter(
            (answer) => !!answer.normalizedRating
          );

          const baseDatum = {
            user: collaboratorAnswer.user.name,
            reportId,
            date: new Date(collaboratorAnswer.report.createdAt),
          };

          // Dumb guard to prevent fucked up dataset to display
          if (baseDatum.date.getTime() === 0) return [];
          return [
            {
              ...baseDatum,
              gradeType: "collaborator",
              grade: collaboratorAnswer.normalizedRating,
            },
            {
              ...baseDatum,
              gradeType: "referents",
              grade:
                nonNullReferentAnswers.length > 0
                  ? getClosestGrade(
                      options,
                      meanBy(nonNullReferentAnswers, "normalizedRating")
                    ).value
                  : null,
            },
          ];
        }
      );

      return sortBy(data, ["date"]);
    }
    return null;
  }, [answers, userId]);
}
